import React from "react";
import { useDispatch, useSelector } from "react-redux";

import { createStyles, makeStyles } from "@material-ui/core/styles";
import Box from "@material-ui/core/Box";
import Button from "@material-ui/core/Button";
import ButtonGroup from "@material-ui/core/ButtonGroup";
import FileCopyOutlinedIcon from "@material-ui/icons/FileCopyOutlined";
import Grid from "@material-ui/core/Grid";
import Link from "@material-ui/core/Link";
import OpenInNewIcon from "@material-ui/icons/OpenInNew";
import Paper from "@material-ui/core/Paper";
import Typography from "@material-ui/core/Typography";
import VisibilityIcon from "@material-ui/icons/Visibility";
import VisibilityOffIcon from "@material-ui/icons/VisibilityOff";

import * as actions from "../../redux/actions/index";
import copyShareUrl from "../../util/copyShareUrl";
import HideTextForSmall from "../Shared/HideTextForSmall";
import OverlayLoading from "../Shared/OverlayLoading";
import serverSelect from "../../serverSelect";
import authHeader from "../../services/auth-header";
import axios from "../../util/axios-reportonce";

const useStyles = makeStyles((theme) =>
    createStyles({
        buttonWrapper: {
            display: "flex",
            justifyContent: "space-between",
            marginBottom: theme.spacing(3),
        },
        inlineIcon: {
            verticalAlign: "sub",
        },
        paper: {
            padding: theme.spacing(3),
            position: "relative",
        },
        buttonText: {
            color: theme.palette.primary.contrastText,
        },
    })
);

export default function MenuBar(props) {
    const listing = useSelector((state) => state.listing.details);
    const loading = useSelector((state) => state.listing.loading);
    const listingId = useSelector((state) => state.listing.id);
    const currentPage = useSelector((state) => state.listings.currentPage);
    const user = useSelector((state) => state.user);
    const dispatch = useDispatch();
    const classes = useStyles();
    const server = serverSelect();
    const publishedDate = listing?.dashboard?.modified
    const request_headers = authHeader(user);
    const listingCommentCount = !loading ? listing.comments.length : "\u2026";
    const publishedIcon = !loading ? (
        listing.dashboard.published ? (
            <VisibilityIcon
                className={classes.inlineIcon}
                color="primary"
                titleAccess="Published"
            />
        ) : (
            <VisibilityOffIcon
                className={classes.inlineIcon}
                color="disabled"
                titleAccess="Unpublished"
            />
        )
    ) : (
        ""
    );
    const listingStatus = !loading
        ? listing.dashboard.published
            ? "Published"
            : "Unpublished"
        : "\u2026";

    const togglePublishedStatus = () => {
        if (!listing.dashboard.published) {
            // PUBLISH
            if (listing.dashboard.created) {
                // Check if this listing has previously been published.
                // This listing has previously been published, so we must use put to publish again.
                axios
                    .put(
                        `${server}/api/listingsdashboard/${listingId}/`,
                        {
                            listonce_id: listingId,
                            published: true,
                            created_by: user.userData.id,
                            theme: user.userData.theme,
                        },
                        request_headers
                    )
                    .then(function (response) {
                        // handle success
                        // console.log('response = ', response);
                        dispatch(
                            actions.showSnackbar(
                                "Report published successfully",
                                "success"
                            )
                        );
                        dispatch(actions.loadSpecificListing(listingId));
                        dispatch(actions.loadListings(currentPage));
                    })
                    .catch(function (error) {
                        dispatch(
                            actions.showSnackbar(
                                "Error publishing Report",
                                "error"
                            )
                        );
                    })
                    .then(function () {
                        // always executed
                    });
            } else {
                // This listing has never been published, so we must use post to publish for the first time.
                axios
                    .post(
                        `${server}/api/listingsdashboard/`,
                        {
                            listonce_id: listingId,
                            created_by: user.userData.id,
                            theme: user.userData.theme,
                            email: user.userData.email,
                            internal: true
                        },
                        request_headers
                    )
                    .then(function (response) {
                        // handle success
                        // console.log('response = ', response);
                        dispatch(
                            actions.showSnackbar(
                                "Report published successfully",
                                "success"
                            )
                        );
                        dispatch(actions.loadSpecificListing(listingId));
                        dispatch(actions.loadListings(currentPage));
                    })
                    .catch(function (error) {
                        // handle error
                        // console.log(error);
                        dispatch(
                            actions.showSnackbar(
                                "Error publishing Report",
                                "error"
                            )
                        );
                    })
                    .then(function () {
                        // always executed
                    });
            }
        } else {
            // UNPUBLISH
            axios
                .put(
                    `${server}/api/listingsdashboard/${listingId}/`,
                    {
                        published: false,
                        listonce_id: listingId,
                        created_by: user.userData.id,
                        theme: user.userData.theme,
                    },
                    request_headers
                )
                .then(function (response) {
                    // handle success
                    // console.log('response = ', response);
                    dispatch(
                        actions.showSnackbar(
                            "Report unpublished successfully",
                            "success"
                        )
                    );
                    dispatch(actions.loadSpecificListing(listingId));
                    dispatch(actions.loadListings(currentPage));
                })
                .catch(function (error) {
                    // handle error
                    // console.log(error);
                    dispatch(
                        actions.showSnackbar(
                            "Error unpublishing Report",
                            "error"
                        )
                    );
                })
                .then(function () {
                    // always executed
                });
        }
    };
    return (
        <Grid item xs={12} md={6}>
            <Paper className={classes.paper}>
                <Box className={classes.buttonWrapper}>
                    <Button
                        variant="contained"
                        color="primary"
                        disabled={loading}
                        onClick={() => togglePublishedStatus()}
                    >
                        {!loading
                            ? listing.dashboard.published
                                ? "Unpublish Report"
                                : "Publish Report"
                            : "Publish Report"}
                    </Button>
                    <ButtonGroup>
                        <Button
                            variant="contained"
                            color="primary"
                            disabled={
                                loading ||
                                (listing !== null &&
                                    listing.dashboard !== null &&
                                    listing.dashboard.published === false)
                                    ? true
                                    : false
                            }
                            endIcon={<FileCopyOutlinedIcon />}
                            onClick={() =>
                                copyShareUrl(
                                    listing.dashboard.public_dashboard_url_v2,
                                    (message, severity) =>
                                        dispatch(
                                            actions.showSnackbar(
                                                message,
                                                severity
                                            )
                                        )
                                )
                            }
                        >
                            <HideTextForSmall>Copy URL</HideTextForSmall>
                        </Button>
                        <Button
                            className={classes.buttonText}
                            variant="contained"
                            color="primary"
                            disabled={
                                loading ||
                                (listing !== null &&
                                    listing.dashboard !== null &&
                                    listing.dashboard.published === false)
                                    ? true
                                    : false
                            }
                            target="_blank"
                            href={`/dashboard/listing/${listing?.dashboard?.ekey}/${listing?.listonce_id}`}
                            component={Link}
                            endIcon={<OpenInNewIcon />}
                        >
                            <HideTextForSmall>View Report</HideTextForSmall>
                        </Button>
                    </ButtonGroup>
                </Box>
                <Box className={classes.buttonWrapper}>
                <Button
                    className={classes.buttonText}
                    variant="contained"
                    color="primary"
                    disabled={
                        loading ||
                        (listing !== null &&
                            listing.dashboard !== null &&
                            listing.dashboard.published === false)
                            ? true
                            : false
                    }
                    target="_blank"
                    href={`/dashboard/listing/${listing?.listonce_id}/${listing?.dashboard?.ekey}/`}
                    component={Link}
                    endIcon={<OpenInNewIcon />}
                >
                    <HideTextForSmall>View Report v1</HideTextForSmall>
                </Button>
                </Box>
                <Typography variant="body2" style={{fontWeight: 350}}>
                    Status: {publishedIcon} {listingStatus}
                </Typography>
                <Typography variant="body2" style={{fontWeight: 350}}>Listing ID: {listingId}</Typography>
                <Typography variant="body2" style={{fontWeight: 350}}>
                    Comments: {listingCommentCount}
                </Typography>
                {listing?.dashboard?.published === true ? 
                    <Typography variant="body2" style={{fontWeight: 350}}>Published: {publishedDate}</Typography> : ""}
                <OverlayLoading loading={loading} />
            </Paper>
        </Grid>
    );
}
